// Recharts wrapper for a categorical bar chart. Themed for the Loop dark
// palette. Supports vertical and horizontal layouts; defaults to vertical.

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  chartPalette,
  chartTheme,
  tokens,
  tooltipContentStyle,
  tooltipItemStyle,
  tooltipLabelStyle,
} from "../lib/theme";

export interface BarPoint {
  x: string;
  [series: string]: string | number | null;
}

export interface ChartBarProps {
  data: BarPoint[];
  bars: Array<{ key: string; name: string; color?: string }>;
  height?: number;
  stacked?: boolean;
  /** "horizontal" places categories on the Y axis (good for long labels). */
  layout?: "vertical" | "horizontal";
  /** Show value labels on each bar (single-series only). */
  showValues?: boolean;
}

export default function ChartBar({
  data,
  bars,
  height = 240,
  stacked = false,
  layout = "vertical",
  showValues = false,
}: ChartBarProps) {
  const isHorizontal = layout === "horizontal";
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={isHorizontal ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 12, bottom: 0, left: isHorizontal ? 8 : 0 }}
      >
        <CartesianGrid
          stroke={chartTheme.grid}
          strokeDasharray="3 6"
          horizontal={!isHorizontal}
          vertical={isHorizontal}
        />
        {isHorizontal ? (
          <>
            <XAxis
              type="number"
              stroke={chartTheme.axis}
              tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="x"
              stroke={chartTheme.axis}
              tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
              tickLine={false}
              axisLine={{ stroke: chartTheme.grid }}
              width={140}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="x"
              stroke={chartTheme.axis}
              tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
              tickLine={false}
              axisLine={{ stroke: chartTheme.grid }}
              minTickGap={16}
            />
            <YAxis
              stroke={chartTheme.axis}
              tick={{ fill: chartTheme.axisText, fontSize: tokens.font.size.xs }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
          </>
        )}
        <Tooltip
          contentStyle={tooltipContentStyle}
          labelStyle={tooltipLabelStyle}
          itemStyle={tooltipItemStyle}
          cursor={{ fill: "rgba(0,212,170,0.06)" }}
        />
        {bars.length > 1 && (
          <Legend
            wrapperStyle={{
              color: tokens.color.fg1,
              fontSize: tokens.font.size.sm,
              paddingTop: tokens.space.sm,
            }}
            iconType="circle"
          />
        )}
        {bars.map((b, i) => {
          const fill: string =
            b.color ??
            chartPalette[i % chartPalette.length] ??
            chartPalette[0] ??
            tokens.color.tealLight;
          const props = stacked ? { stackId: "stack" } : {};
          return (
            <Bar
              key={b.key}
              dataKey={b.key}
              name={b.name}
              fill={fill}
              radius={isHorizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
              {...props}
            >
              {/* Per-cell brand fill for single-series so colors don't repeat. */}
              {bars.length === 1 &&
                data.map((_, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={chartPalette[idx % chartPalette.length] ?? fill}
                  />
                ))}
              {showValues && bars.length === 1 && (
                <LabelList
                  dataKey={b.key}
                  position={isHorizontal ? "right" : "top"}
                  style={{
                    fill: tokens.color.fg0,
                    fontSize: tokens.font.size.xs,
                    fontWeight: 600,
                    fontFamily: tokens.font.mono,
                  }}
                />
              )}
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
}
